import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppShell } from '../contexts/AppShellContext';
import { DEMO_CAMPAIGNS, DEMO_JOBS, daysUntil, type CampaignType, type CampaignStatus, type DemoCampaign } from '../data/demoProxyData';

// Which campaign to pre-select for each demo step of Idea 3
const STEP_TO_CAMPAIGN: Record<number, string> = {
  1: 'c-4010016', // Cormorant – shortage, first item
  2: 'c-4010016', // same – shows solicitor grouping
  3: 'c-4010016', // same – shows pre-filled deficit
  4: 'c-4010016', // same – shows Approve button
  5: 'c-9100022', // Granite Peak – 9 days, POD window
  6: 'c-3025905', // Laurentian Bank – 5-day email-only
  7: 'c-4010009', // Kestrel – 4 days, abort
  8: 'c-9100021', // Coastal Marine – blocked
};

type FilterChip = CampaignType | CampaignStatus | 'all';

const TYPE_COLOR: Record<CampaignType, { bg: string; fg: string }> = {
  shortage: { bg: '#FEF2F2', fg: '#b91c1c' },
  yellow:   { bg: '#FFFBEB', fg: '#92400e' },
  red:      { bg: '#FFF7ED', fg: '#9a3412' },
};

const STATUS_DOT: Record<CampaignStatus, string> = {
  ready:     '#14b8a6',
  scheduled: '#f59e0b',
  blocked:   '#e7000b',
};

function deadlineBanner(days: number, hasFile: boolean): { type: string; msg: string } | null {
  if (days < 5)  return { type: 'abort',     msg: `Under 5 days (${days}d) — this job is a candidate for abort per ICT-8. Recording a recommendation does not close the job.` };
  if (days === 5) return { type: 'email',    msg: `5-day mark — stop physical distribution. Proceed email-only unless sets are already in the warehouse.` };
  if (days <= 10 && !hasFile) return { type: 'pod',  msg: `Inside 10-day window — print on demand allowed when ≤100 pages and ≤50 sets. Page count not yet on file.` };
  if (days <= 10 && hasFile)  return { type: 'pod',  msg: `Inside 10-day window — print on demand allowed when ≤100 pages and ≤50 sets.` };
  return null;
}

function buildBody(campaign: DemoCampaign): string {
  const job = DEMO_JOBS[campaign.jobId];
  if (!job) return '';
  const days = daysUntil(job.meeting);
  const meetFmt = new Date(job.meeting + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  if (campaign.type === 'shortage') {
    const deficit = (job.ordered ?? 0) - (job.received ?? 0);
    return `Dear ${job.solicitor} team,\n\nThis is a follow-up regarding outstanding materials for ${job.issuer} (Job ${campaign.jobId}, CUSIP ${job.cusip}).\n\nOrdered: ${job.ordered} sets\nReceived: ${job.received} sets\nDeficit_Quantity: ${deficit} sets\n\nThe annual meeting is scheduled for ${meetFmt}. Please advise on the expected ship date for the remaining ${deficit} set(s).\n\nWe will follow up daily until the full order is received, per ICT-13.\n\nBest regards,\nICT Shared Services`;
  }
  if (campaign.type === 'yellow') {
    return `Dear ${job.solicitor} team,\n\nYellow follow-up for ${job.issuer} (Job ${campaign.jobId}, CUSIP ${job.cusip}).\n\nMeeting date: ${meetFmt} (${days} days remaining)\nRecord-date quantity on file: ${job.rdqSets?.toLocaleString() ?? '—'} sets\n\nPlease confirm the release date and that the quantity on file matches your records before we release to print.\n\nBest regards,\nICT Shared Services`;
  }
  // red
  return `Dear ${job.solicitor} team,\n\nRed follow-up — urgent: ${job.issuer} (Job ${campaign.jobId}, CUSIP ${job.cusip}).\n\nMeeting date: ${meetFmt} (${days} days remaining)\n\nThe proxy statement is not yet on file. Please advise immediately on filing status and whether print on demand applies (ICT-8: ≤100 pages, ≤50 sets).\n\nBest regards,\nICT Shared Services`;
}

// Group campaigns by solicitor
function groupBySolicitor(campaigns: DemoCampaign[]): { solicitor: string; email: string; items: DemoCampaign[] }[] {
  const map = new Map<string, { email: string; items: DemoCampaign[] }>();
  for (const c of campaigns) {
    const job = DEMO_JOBS[c.jobId];
    if (!job) continue;
    if (!map.has(job.solicitor)) map.set(job.solicitor, { email: job.solicitorEmail, items: [] });
    map.get(job.solicitor)!.items.push(c);
  }
  const ORDER = ['Broadridge ICS', 'Broadridge Canada', 'Georgeson', 'Morrow Sodali', 'Alliance Advisors', 'D.F. King', 'Mediant Communications'];
  return [...map.entries()]
    .sort(([a], [b]) => (ORDER.indexOf(a) - ORDER.indexOf(b)))
    .map(([solicitor, v]) => ({ solicitor, ...v }));
}

export function ProxyOutreachPage() {
  const { setTopbar } = useAppShell();
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState<FilterChip>('all');
  const [campaigns, setCampaigns] = useState<DemoCampaign[]>(DEMO_CAMPAIGNS);
  const [selectedId, setSelectedId] = useState<string>(DEMO_CAMPAIGNS[0].id);
  const [auditLog, setAuditLog] = useState<string[]>([]);

  useEffect(() => {
    setTopbar('Outreach Engine', 'Follow-up campaigns grouped by solicitor — Yellow, Red, Shortage');
  }, [setTopbar]);

  // React to demo step: auto-select the right campaign for each step
  useEffect(() => {
    const idea = parseInt(searchParams.get('idea') ?? '0', 10);
    const step = parseInt(searchParams.get('step') ?? '0', 10);
    if (idea !== 3 || !step) return;
    const campaignId = STEP_TO_CAMPAIGN[step];
    if (campaignId) setSelectedId(campaignId);
    // For step 5/6/7 switch to red filter to show those campaigns; step 3 shortage
    if (step === 3) setFilter('shortage');
    else if (step === 5 || step === 6 || step === 7) setFilter('red');
    else setFilter('all');
  }, [searchParams]);

  const filteredCampaigns = campaigns.filter(c => {
    if (filter === 'all') return true;
    if (['shortage', 'yellow', 'red'].includes(filter)) return c.type === filter;
    return c.status === filter;
  });

  const groups = groupBySolicitor(filteredCampaigns);
  const selected = campaigns.find(c => c.id === selectedId) ?? campaigns[0];
  const selectedJob = selected ? DEMO_JOBS[selected.jobId] : null;
  const days = selectedJob ? daysUntil(selectedJob.meeting) : 0;
  const banner = selectedJob ? deadlineBanner(days, false) : null;
  const body = selected ? buildBody(selected) : '';

  function approve() {
    setCampaigns(prev => prev.map(c => c.id === selectedId ? { ...c, status: 'ready', sentAt: '14:22 ET' } : c));
    setAuditLog(prev => [`${new Date().toLocaleTimeString()} — Approved & queued: ${selectedJob?.issuer} (${selected?.type})`, ...prev]);
  }

  function schedule() {
    setCampaigns(prev => prev.map(c => c.id === selectedId ? { ...c, status: 'scheduled', scheduledLabel: '14:00 ET' } : c));
    setAuditLog(prev => [`${new Date().toLocaleTimeString()} — Scheduled 14:00 ET: ${selectedJob?.issuer}`, ...prev]);
  }

  const CHIPS: { key: FilterChip; label: string; count: number }[] = [
    { key: 'all',       label: 'All',             count: campaigns.length },
    { key: 'shortage',  label: 'Shortage',         count: campaigns.filter(c => c.type === 'shortage').length },
    { key: 'yellow',    label: 'Yellow FU',        count: campaigns.filter(c => c.type === 'yellow').length },
    { key: 'red',       label: 'Red FU',           count: campaigns.filter(c => c.type === 'red').length },
    { key: 'ready',     label: 'Ready to send',    count: campaigns.filter(c => c.status === 'ready').length },
    { key: 'scheduled', label: 'Scheduled',        count: campaigns.filter(c => c.status === 'scheduled').length },
    { key: 'blocked',   label: 'Blocked',          count: campaigns.filter(c => c.status === 'blocked').length },
  ];

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Filter chips */}
      <div data-tour="outreach-filters" className="px-6 pt-5 pb-3 flex gap-1.5 flex-wrap border-b border-trim">
        {CHIPS.map(({ key, label, count }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className="px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border"
            style={{
              background:  filter === key ? '#043243' : 'white',
              color:       filter === key ? 'white'   : '#595A60',
              borderColor: filter === key ? '#043243' : '#E5E7EB',
            }}
          >
            {label} <span className="opacity-60 ml-1">{count}</span>
          </button>
        ))}
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left: campaign list */}
        <div className="w-72 flex-shrink-0 border-r border-trim overflow-y-auto bg-[#F8FAFC]">
          {groups.length === 0 ? (
            <div className="p-6 text-xs text-muted">No campaigns match this filter.</div>
          ) : groups.map(g => (
            <div key={g.solicitor} data-tour="solicitor-group">
              <div className="px-4 pt-4 pb-1">
                <p className="text-xs font-semibold text-ink">{g.solicitor}</p>
                <p className="text-[10px] text-muted">{g.email} · {g.items.length} job{g.items.length !== 1 ? 's' : ''}</p>
              </div>
              {g.items.map(c => {
                const job = DEMO_JOBS[c.jobId];
                const isActive = c.id === selectedId;
                const tc = TYPE_COLOR[c.type];
                return (
                  <button
                    key={c.id}
                    onClick={() => setSelectedId(c.id)}
                    className="w-full text-left px-4 py-2.5 flex items-center gap-2.5 transition-colors border-l-2"
                    style={{
                      background:   isActive ? 'white'       : 'transparent',
                      borderColor:  isActive ? '#14b8a6'     : 'transparent',
                    }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: STATUS_DOT[c.status] }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-ink truncate">{job?.issuer}</p>
                      <p className="text-[10px] text-muted">Job {c.jobId}</p>
                    </div>
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: tc.bg, color: tc.fg }}>
                      {c.type}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Right: composer */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {selected && selectedJob ? (
            <>
              {/* Deadline banner */}
              {banner && (
                <div
                  data-tour="deadline-banner"
                  className="mb-4 px-4 py-3 rounded-xl text-xs leading-relaxed border"
                  style={{
                    background:  banner.type === 'abort' ? '#FEF2F2' : banner.type === 'email' ? '#EFF6FF' : '#FFFBEB',
                    color:       banner.type === 'abort' ? '#991b1b' : banner.type === 'email' ? '#1e40af' : '#92400e',
                    borderColor: banner.type === 'abort' ? '#FECACA' : banner.type === 'email' ? '#BFDBFE' : '#FCD34D',
                  }}
                >
                  <span className="font-semibold mr-1">
                    {banner.type === 'abort' ? 'Under 5 days' : banner.type === 'email' ? '5-day mark' : '10-day window'}
                  </span>
                  {banner.msg}
                </div>
              )}

              {/* Email header */}
              <div data-tour="composer-letter" className="rounded-2xl border border-trim overflow-hidden mb-4">
                <div className="px-4 py-3 border-b border-trim bg-[#F8FAFC] flex items-center gap-2">
                  <span className="text-xs font-semibold text-ink">To:</span>
                  <span className="text-xs text-muted">{selectedJob.solicitorEmail}</span>
                  <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full"
                    style={{ background: TYPE_COLOR[selected.type].bg, color: TYPE_COLOR[selected.type].fg }}>
                    {selected.type}
                  </span>
                </div>
                <div className="px-4 py-3 border-b border-trim">
                  <span className="text-xs font-semibold text-ink">Subject:</span>
                  <span className="text-xs text-muted ml-2">
                    {selected.type === 'shortage' ? `Material shortage — ${selectedJob.issuer} (Job ${selected.jobId})` :
                     selected.type === 'yellow' ? `Yellow follow-up — ${selectedJob.issuer} (Job ${selected.jobId})` :
                     `Red follow-up — ${selectedJob.issuer} (Job ${selected.jobId})`}
                  </span>
                </div>
                <div className="px-4 py-4">
                  <pre className="text-xs text-ink leading-relaxed whitespace-pre-wrap font-sans">{body}</pre>
                </div>
              </div>

              {/* Actions */}
              <div data-tour="composer-controls" className="flex items-center gap-2 flex-wrap">
                {selected.status === 'blocked' ? (
                  <div className="px-4 py-2 rounded-lg text-xs text-red-700 bg-red-50 border border-red-200">
                    Outbound blocked — bounced address on file. Review the bounce in Exceptions first.
                  </div>
                ) : (
                  <>
                    <button
                      onClick={approve}
                      className="px-4 py-2 rounded-lg text-xs font-semibold text-white"
                      style={{ background: '#14b8a6' }}
                    >
                      {selected.status === 'scheduled' ? `Scheduled ${selected.scheduledLabel}` : 'Approve & send'}
                    </button>
                    <button
                      onClick={schedule}
                      className="px-4 py-2 rounded-lg text-xs font-semibold border border-trim text-ink hover:bg-[#F8FAFC] transition-colors"
                    >
                      Schedule
                    </button>
                    {banner?.type === 'abort' && (
                      <button
                        onClick={() => setAuditLog(prev => [`${new Date().toLocaleTimeString()} — Abort recommendation recorded: ${selectedJob.issuer}`, ...prev])}
                        className="px-4 py-2 rounded-lg text-xs font-semibold border border-red-200 text-red-700 hover:bg-red-50 transition-colors"
                      >
                        Record abort recommendation
                      </button>
                    )}
                    {banner?.type === 'pod' && (
                      <button
                        onClick={() => setAuditLog(prev => [`${new Date().toLocaleTimeString()} — POD request recorded: ${selectedJob.issuer}`, ...prev])}
                        className="px-4 py-2 rounded-lg text-xs font-semibold border border-amber-200 text-amber-700 hover:bg-amber-50 transition-colors"
                      >
                        Record POD request
                      </button>
                    )}
                  </>
                )}
              </div>

              {auditLog.length > 0 && (
                <div className="mt-5 rounded-xl border border-trim overflow-hidden">
                  <div className="px-4 py-2 bg-[#F8FAFC] border-b border-trim text-xs font-semibold text-muted">Session log</div>
                  {auditLog.map((entry, i) => (
                    <div key={i} className="px-4 py-2 text-xs text-ink border-b border-trim last:border-0">{entry}</div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="text-xs text-muted">Select a campaign to compose.</div>
          )}
        </div>
      </div>
    </div>
  );
}
